import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChartBarIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon,
  CheckCircleIcon, ClockIcon, BoltIcon, AcademicCapIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// File Health — roster-wide Employment File completeness dashboard.
// Phase 4 of the consolidation. Pre-launch tool: see at a glance who
// has gaps before sending the "everyone reconcile your file" wave,
// and click through to each person's file from one place.

const ROLE_LABELS = {
  director: 'Director',
  admin: 'Office Admin',
  assistant_director: 'Asst. Director',
  teacher: 'Teacher',
  float: 'Float',
  cook: 'Cook',
};

function percent(verified, total) {
  if (!total) return 0;
  return Math.round((verified / total) * 100);
}

// STEP 5 — CREDENTIALS ON FILE.
//
// `staff_documents` rows carrying an expiry. They are NOT requirements and they
// COUNT TOWARD NOTHING: `percent()` and `healthColor()` below read only
// `verified`, `total` and `expired`, every one of which the API computes from
// `onboarding_progress` alone. A credential can never move a bar, a colour, a
// summary tile or a filter.
//
// DO NOT add credentials to `summary` or to the completeness column. A CDA
// satisfies no requirement in this portal because no requirement for it exists.
const CRED_CATEGORY_LABELS = {
  training_cert: 'Training certificate',
  compliance_doc: 'Compliance document',
  other: 'Credential',
};

// Date-only formatting, never `new Date(...)` on a YYYY-MM-DD string — that is
// the display bug fixed on 2026-09-12 (node-postgres parses a date at local
// midnight and every browser west of UTC read it back a day early).
function credDate(value) {
  const iso = String(value || '').slice(0, 10);
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '—';
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m)-1]} ${Number(d)}, ${y}`;
}

function healthColor(pct, expired) {
  if (expired > 0) return 'bg-red-500';
  if (pct === 100) return 'bg-green-500';
  if (pct >= 70) return 'bg-amber-400';
  return 'bg-red-400';
}

export default function FileHealthPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/file-health/roster');
      setRows(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load roster health.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const filtered = rows.filter(r => {
    if (roleFilter !== 'all' && r.role !== roleFilter) return false;
    if (statusFilter === 'gaps' && r.verified === r.total && r.expired === 0) return false;
    if (statusFilter === 'expired' && r.expired === 0) return false;
    if (statusFilter === 'pending_admin' && r.pending_admin === 0) return false;
    if (statusFilter === 'complete' && (r.verified !== r.total || r.expired > 0)) return false;
    return true;
  });

  const summary = {
    total: rows.length,
    complete: rows.filter(r => r.verified === r.total && r.expired === 0).length,
    with_gaps: rows.filter(r => r.verified !== r.total).length,
    with_expired: rows.filter(r => r.expired > 0).length,
    pending_admin: rows.filter(r => r.pending_admin > 0).length,
  };

  return (
    <div className="animate-slide-up max-w-6xl mx-auto">
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <ChartBarIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">File Health</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Where is everyone's Employment File at? Use this to plan the current-staff rollout — backfill gaps,
            chase pending verifications, and renew expired docs before the next nudge wave goes out.
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <SummaryTile label="Total active" value={summary.total} color="gray" icon={ChartBarIcon} />
        <SummaryTile label="Complete" value={summary.complete} color="green" icon={CheckCircleIcon} />
        <SummaryTile label="With gaps" value={summary.with_gaps} color="amber" icon={BoltIcon} />
        <SummaryTile label="Waiting on admin" value={summary.pending_admin} color="blue" icon={ClockIcon} />
        <SummaryTile label="Expired docs" value={summary.with_expired} color="red" icon={ExclamationTriangleIcon} />
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-gray-500 uppercase">Filter:</span>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 px-3 py-1.5 bg-white">
          <option value="all">Every role</option>
          {Object.entries(ROLE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 px-3 py-1.5 bg-white">
          <option value="all">Everyone</option>
          <option value="gaps">Has gaps</option>
          <option value="pending_admin">Waiting on admin</option>
          <option value="expired">Expired docs</option>
          <option value="complete">Complete files</option>
        </select>
        <span className="text-xs text-gray-500 ml-auto">Showing {filtered.length} of {rows.length}.</span>
      </div>

      {/* Roster table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100 text-left">
              <th className="px-4 py-2.5 font-semibold text-gray-700 text-xs uppercase tracking-wider">Staff</th>
              <th className="px-4 py-2.5 font-semibold text-gray-700 text-xs uppercase tracking-wider">Role</th>
              <th className="px-4 py-2.5 font-semibold text-gray-700 text-xs uppercase tracking-wider">Status</th>
              <th className="px-4 py-2.5 font-semibold text-gray-700 text-xs uppercase tracking-wider">Completeness</th>
              <th className="px-4 py-2.5 font-semibold text-gray-700 text-xs uppercase tracking-wider">Signals</th>
              <th className="px-4 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(r => {
              const pct = percent(r.verified, r.total);
              return (
                <tr key={r.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2.5">
                    <Link to={`/staff/${r.id}`} className="font-medium text-gray-900 hover:text-orange-600">
                      {r.first_name} {r.last_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{ROLE_LABELS[r.role] || r.role}</td>
                  <td className="px-4 py-2.5 text-gray-600 capitalize text-xs">
                    {(r.status || '').replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-2.5 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className={`h-full ${healthColor(pct, r.expired)}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-600 tabular-nums">{r.verified}/{r.total}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {r.expired > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
                          {r.expired} expired
                        </span>
                      )}
                      {r.pending_admin > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                          {r.pending_admin} waiting admin
                        </span>
                      )}
                      {r.not_started > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-gray-50 text-gray-600 border-gray-200">
                          {r.not_started} not started
                        </span>
                      )}
                      {r.verified === r.total && r.expired === 0 && r.total > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">
                          Complete
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Link to={`/staff/${r.id}`}
                      className="text-xs font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
                      Open file <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500 text-sm">No staff match the current filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Credentials on file — their own section, counting toward nothing. */}
      <CredentialsSection rows={filtered} />

      <p className="text-xs text-gray-500 mt-4">
        Tip: set <code>SUPPRESS_ONBOARDING_EMAILS=true</code> on the App Runner service while you backfill so existing staff don't get noisy emails. Flip it back off before sending the "please review your file" wave.
      </p>
    </div>
  );
}

function SummaryTile({ label, value, color, icon: Icon }) {
  const colorMap = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`border rounded-2xl p-3 ${colorMap[color] || colorMap.gray}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

// ─── Credentials on file ────────────────────────────────────────────
//
// Qualifications a staff member holds that no requirement in this portal asks
// for — CDAs, instructor certifications. Tracked for expiry and shown here so
// they stop being invisible; counted toward nobody's completeness.
//
// Before this shipped these appeared in no report, no bucket and no count
// anywhere. Liz Tucker's CDA expiring 2026-12-09 was the whole test.
function CredentialsSection({ rows }) {
  const items = [];
  for (const r of rows) {
    for (const c of (r.credentials || [])) {
      items.push({ ...c, staff: `${r.first_name} ${r.last_name}`, staff_id: r.id });
    }
  }
  items.sort((a, b) => String(a.expiration_date).localeCompare(String(b.expiration_date)));

  const todayISO = new Date().toISOString().slice(0, 10);
  const in60ISO = new Date(Date.now() + 60 * 864e5).toISOString().slice(0, 10);

  if (items.length === 0) return null;

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2.5">
        <AcademicCapIcon className="w-5 h-5 text-indigo-600" />
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Credentials on file</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Qualifications staff hold that no requirement asks for. Tracked for expiry only —
            these count toward nobody&rsquo;s file completeness.
          </p>
        </div>
        <span className="ml-auto text-xs text-gray-500 tabular-nums">{items.length}</span>
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {items.map(c => {
            const iso = String(c.expiration_date).slice(0, 10);
            const expired = iso < todayISO;
            const soon = !expired && iso <= in60ISO;
            return (
              <tr key={`cred-${c.id}`} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5">
                  <Link to={`/staff/${c.staff_id}`} className="font-medium text-gray-900 hover:text-orange-600">
                    {c.staff}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-gray-600">
                  {CRED_CATEGORY_LABELS[c.category] || 'Credential'}
                  {c.original_filename && (
                    <span className="block text-xs text-gray-400 truncate max-w-[22rem]">{c.original_filename}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-600 tabular-nums whitespace-nowrap">
                  {credDate(c.expiration_date)}
                </td>
                <td className="px-4 py-2.5">
                  {expired && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
                      Expired
                    </span>
                  )}
                  {soon && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                      Expires within 60 days
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
